import { UpdateBrandVDriveRequestData } from '../types/api/brand.types';
import {
  ActivateVDriveRequestData,
  ActivateVDriveV2Dto,
  GetVDriveResponse,
  InitiateVDriveVerificationProcessForPartnerRequestData,
  SendReportVDRiveRequestData,
  StartLocationVerificationProcessVDriveRequestData,
  UpdateCreditCardRequestData,
  UpdateCreditCardV2Dto,
  UpdateVDriveCreditCardRequestData,
  SetVDriveSecondaryCardRequest,
  LocationFleetOrganizationStatus,
  VDriveFleetOrganization,
  VDriveFleetOrganizationsParams,
  UpdateVDriveOverdraftLimitRequest,
  UpdateVDrivePaymentModeRequest,
  VDriveStrategySuggestionJob,
  VDriveStrategySuggestionRequest,
  VDriveStrategySuggestionScope,
} from '../types/api/vDrive.types';
import {
  ManageLocationVDriveRequestData,
  VDriveAdminWalletResponse,
} from '../types/api/wallet.types';
import { BrandVDriveEntity } from '../types/brand.types';
import {
  FundedTipScheduleEntity,
  LocationVDriveEntity,
} from '../types/location.types';
import {
  Captcha,
  DateRangeParams,
  PaginationParams,
  ResponseType,
} from '../types/shared.types';
import { api } from './api';

export const getLocationVDriveList = (
  params: PaginationParams<{ query?: string }>
) =>
  api.get<ResponseType<Array<LocationVDriveEntity>>>(`/v1/vdrive`, { params });

export const getVDriveWallet = (
  params: DateRangeParams | Record<string, never>
) =>
  api.get<ResponseType<VDriveAdminWalletResponse>>(`/v1/vdrive/wallet`, {
    params,
  });

export const getLocationVDriveById = (locationId: string | number) =>
  api.get<ResponseType<LocationVDriveEntity>>(
    `/v1/vdrive/location/${locationId}`
  );

export const updateLocationVDrive = (
  locationId: string | number,
  data: ManageLocationVDriveRequestData
) => api.put<ResponseType<null>>(`/v1/vdrive/location/${locationId}`, data);

/**
 * Admin only. How far this balance may go negative when the owner's card
 * fails; `null` resets it to the default. Answers with the whole vDrive.
 */
export const updateVDriveOverdraftLimit = (
  locationId: string | number,
  data: UpdateVDriveOverdraftLimitRequest
) =>
  api.put<ResponseType<LocationVDriveEntity>>(
    `/v1/vdrive/${locationId}/overdraft-limit`,
    data
  );

/**
 * Admin and support: per delivery or top-up, for the whole balance (every
 * location sharing it). Written to the audit log. Answers with the vDrive.
 */
export const updateVDrivePaymentMode = (
  locationId: string | number,
  data: UpdateVDrivePaymentModeRequest
) =>
  api.put<ResponseType<LocationVDriveEntity>>(
    `/v1/vdrive/${locationId}/payment-mode`,
    data
  );

export const getLocationVerificationVDriveWithId = (
  locationId: string | number
) =>
  api.get<ResponseType<LocationVDriveEntity>>(
    `/v1/vdrive/location/${locationId}/verification`
  );

export const getVGrubsBrandFundingTipsOrigin = () =>
  api.get<ResponseType<Array<FundedTipScheduleEntity>>>(
    `/v1/vdrive/brand/funding-tip-origin`
  );

/** Fleets that take vGrubs orders, sorted by name, not paginated. */
export const getVDriveFleetOrganizations = (
  params: VDriveFleetOrganizationsParams
) =>
  api.get<ResponseType<VDriveFleetOrganization[]>>(
    `/v1/vdrive/fleet-organizations`,
    { params }
  );

/** Asks vDrive, so it is slower than the rest of the page and can fail alone. */
export const getLocationFleetOrganization = (locationId: string | number) =>
  api.get<ResponseType<LocationFleetOrganizationStatus>>(
    `/v1/vdrive/location/${locationId}/fleet-organization`
  );

export const getBrandVDriveById = (brandId: string | number) =>
  api.get<ResponseType<BrandVDriveEntity>>(`/v1/vdrive/brand/${brandId}`);

export const updateBrandVDrive = (
  brandId: string | number,
  data: UpdateBrandVDriveRequestData
) => api.put<ResponseType<null>>(`/v1/vdrive/brand/${brandId}`, data);

/**
 * Starts the AI's look at a location's or brand's assignment strategy and
 * answers at once with a pending job. While one is running for the same
 * location or brand, this answers with that job instead of starting another.
 * Nothing is saved: the strategy is still saved with the vDrive PUT.
 */
export const startVDriveStrategySuggestion = (
  scope: VDriveStrategySuggestionScope,
  id: string | number,
  data: VDriveStrategySuggestionRequest
) =>
  api.post<ResponseType<VDriveStrategySuggestionJob>>(
    `/v1/vdrive/${scope}/${id}/strategy-suggestion`,
    data
  );

/** Polled for the job; a 404 once it has expired (an hour) or is not this one's. */
export const getVDriveStrategySuggestion = (
  scope: VDriveStrategySuggestionScope,
  id: string | number,
  jobId: string
) =>
  api.get<ResponseType<VDriveStrategySuggestionJob>>(
    `/v1/vdrive/${scope}/${id}/strategy-suggestion/${jobId}`
  );

/**
 * `id` is the LocationVDrive id, never the location id. Rejects every location
 * on that vDrive balance still in verification, all with the same reason, and
 * the owner gets it once by push and in the support chat.
 */
export const rejectVDrive = (id: string | number, reason: string) =>
  api.post<ResponseType<null>>(`/v1/vdrive/${id}/reject`, { reason });

export const disableVDrive = (id: string | number) =>
  api.post<ResponseType<null>>(`/v1/vdrive/${id}/disable`);

export const reactivateVDrive = (id: string | number) =>
  api.post<ResponseType<null>>(`/v1/vdrive/${id}/reactivate`);

export const sendReportVDrive = (data: SendReportVDRiveRequestData) =>
  api.post<ResponseType<null>>(`/v1/vdrive/send-report`, data);

export const startVerificationProcessForLocation = (
  locationId: string | number,
  data: StartLocationVerificationProcessVDriveRequestData
) => api.post<ResponseType<null>>(`/v1/vdrive/${locationId}/start`, data);

export const updateVDriveCreditCardForLocationPartner = (
  locationId: string | number,
  data: UpdateVDriveCreditCardRequestData
) =>
  api.put<ResponseType<{ last4: string }>>(
    `/v2/vdrive/${locationId}/update-location-card`,
    data
  );

export const updateVDriveCreditCardForLocationPartnerWithNewBalance = (
  locationId: string | number,
  data: UpdateCreditCardRequestData
) =>
  api.put<ResponseType<null>>(
    `/v1/vdrive/${locationId}/new-balance-credit-card`,
    data
  );

export const addVDriveBalance = (locationId: string | number, amount: number) =>
  api.post<ResponseType<LocationVDriveEntity>>(
    `/v1/vdrive/${locationId}/balance`,
    {
      amount,
    }
  );

export const initiateVDriveVerificationProcessForPartner = (
  userId: string | number,
  data: InitiateVDriveVerificationProcessForPartnerRequestData
) => api.post<ResponseType<null>>(`/v1/vdrive/${userId}/initiate`, data);

export const updateVDriveCardForPartner = (
  userId: string | number,
  data: UpdateCreditCardRequestData
) => api.put<ResponseType<null>>(`/v1/vdrive/${userId}/update-card`, data);

export const activateVDrive = (id: number, data: ActivateVDriveRequestData) =>
  api.post<ResponseType<null>>(`/v1/vdrive/${id}`, data);

export const increaseVDriveBalance = (
  locationId: string | number,
  data: {
    amount: number;
    adminPassword: string;
    captcha: Captcha;
  }
) =>
  api.put<ResponseType<null>>(
    `/v1/vdrive/location/${locationId}/increase-balance`,
    data
  );

export const deductVDriveBalance = (
  locationId: string | number,
  data: {
    amount: number;
    adminPassword: string;
    captcha: Captcha;
  }
) =>
  api.put<ResponseType<null>>(
    `/v1/vdrive/location/${locationId}/deduct-balance`,
    data
  );

export const getVDriveByIdForPartner = (locationId: string | number) =>
  api.get<ResponseType<GetVDriveResponse>>(`/v2/vdrive/${locationId}`);

export const updateAutoAssignForPartner = (
  autoAssign: boolean,
  locationId: string | number
) =>
  api.put<ResponseType<GetVDriveResponse>>(`/v2/vdrive/${locationId}`, {
    autoAssign,
  });

export const updateAutoRechargeForPartner = (
  autoRechargeAmount: number,
  autoRechargeWhenAmount: number,
  locationId: string | number
) =>
  api.put<ResponseType<GetVDriveResponse>>(
    `/v2/vdrive/${locationId}/auto-recharge`,
    {
      autoRechargeAmount,
      autoRechargeWhenAmount,
    }
  );

/**
 * Merchant, the owner only (403 for staff and managers), on an active vDrive:
 * per delivery or top-up, for the whole balance. A positive balance stays and
 * is spent on the next deliveries when switching to per delivery.
 */
export const updateVDrivePaymentModePartner = (
  locationId: string | number,
  data: UpdateVDrivePaymentModeRequest
) =>
  api.put<ResponseType<GetVDriveResponse>>(
    `/v2/vdrive/${locationId}/payment-mode`,
    data
  );

/**
 * Merchant: add or replace the primary vDrive card. `location-card` rather
 * than `update-card`: owner, manager and staff may call it. With `chargeNow`
 * left out the backend then charges the card for the negative balance plus
 * the auto-recharge; `chargeNow: false` only saves it.
 */
export const updateVDriveLocationCard = (
  locationId: string | number,
  data: UpdateCreditCardV2Dto
) =>
  api.put<ResponseType<GetVDriveResponse>>(
    `/v2/vdrive/${locationId}/location-card`,
    data
  );

/**
 * Merchant: add or replace the secondary vDrive card, charged for the same
 * amount whenever the primary declines. Refused while the location is still
 * on the vGrubs vDrive card.
 */
export const setVDriveSecondaryCardPartner = (
  locationId: string | number,
  data: UpdateCreditCardV2Dto
) =>
  api.put<ResponseType<GetVDriveResponse>>(
    `/v2/vdrive/${locationId}/secondary-card`,
    data
  );

/** Merchant: remove the secondary vDrive card. */
export const removeVDriveSecondaryCardPartner = (locationId: string | number) =>
  api.delete<ResponseType<GetVDriveResponse>>(
    `/v2/vdrive/${locationId}/secondary-card`
  );

/**
 * Merchant: the secondary card becomes the primary, and the other way round.
 * `chargeNow: false` swaps them without charging the new primary for a
 * negative balance.
 */
export const swapVDriveCardsPartner = (
  locationId: string | number,
  data: { chargeNow?: boolean } = {}
) =>
  api.post<ResponseType<GetVDriveResponse>>(
    `/v2/vdrive/${locationId}/swap-cards`,
    data
  );

export const addVDriveBalancePartner = (
  locationId: string | number,
  amount: number
) =>
  api.post<ResponseType<LocationVDriveEntity>>(
    `/v2/vdrive/${locationId}/balance`,
    {
      amount,
    }
  );

export const startVerificationProcessPartner = (
  locationId: string | number,
  data: ActivateVDriveV2Dto
) => api.post<ResponseType<null>>(`/v2/vdrive/${locationId}`, data);

/**
 * Admin: add or replace the location's secondary vDrive card. A Stripe token,
 * like the primary's `update-location-card` (no `isVDriveCard`); the address
 * is optional. Refused while the location is still on the vGrubs vDrive card.
 */
export const setVDriveSecondaryCard = (
  locationId: string | number,
  data: SetVDriveSecondaryCardRequest
) =>
  api.put<ResponseType<LocationVDriveEntity>>(
    `/v1/vdrive/location/${locationId}/secondary-card`,
    data
  );

/**
 * Admin: drop the location's secondary vDrive card. Answers with the updated
 * vDrive (`last4`, `secondaryLast4`).
 */
export const removeVDriveSecondaryCard = (locationId: string | number) =>
  api.delete<ResponseType<LocationVDriveEntity>>(
    `/v1/vdrive/location/${locationId}/secondary-card`
  );

/**
 * Admin: the secondary card becomes the primary and the primary the
 * secondary. The backend then charges for the negative balance plus the
 * auto-recharge amount, as after any card change.
 */
export const swapVDriveCards = (locationId: string | number) =>
  api.post<ResponseType<LocationVDriveEntity>>(
    `/v1/vdrive/location/${locationId}/swap-cards`
  );
